/**
 * The refusals of a decision write that name what they concern — shared by the apps that decide lines (bata-platform ADR 6220):
 * a decision that would leave an article and size above what the distribution centre holds is refused whole with
 * `409 planning.inseason.dc_pool_overcommitted`, and its extension `cells` names each cell with the pairs involved. Nothing was
 * written; this says which cells, in words, beside the shell's message for the code (`api.problem.…`).
 *
 * Here because a cross-feature import is a build failure (G11) and two chapters draw it: «Asignación inicial»'s approvals and
 * Replenishment's decisions.
 */

import { useTranslation } from 'react-i18next';

import { ApiProblemError, useProblemMessage } from '@/api';
import { formatInteger } from '@/lib/format';

import { sizeAbsentReasonKey, sizeNameAliased, useSizeText } from './size-name';

export const DC_POOL_OVERCOMMITTED_CODE = 'planning.inseason.dc_pool_overcommitted';

/**
 * One cell over the limit, as the platform's extension names it: the article by its code and its name, the size by its code and
 * scale (the size key is a one-way hash, never shown). A name the masters do not hold is `null` with its reason.
 */
export interface OvercommittedCell {
  readonly articleKey: string;
  readonly articleName: string | null;
  readonly articleNameAbsentReason: string | null;
  readonly sizeKey: string;
  readonly sizeCode: string | null;
  readonly sizeScale: string | null;
  readonly sizeAbsentReason: string | null;
  /** Pairs promised by open approved lines of «Asignación inicial»; `null` where the refusal does not state it (never nought, ADR 9061). */
  readonly committedUnits: number | null;
  /** What the distribution centre holds of the article and size; `null` where the refusal does not state it. */
  readonly dcUnits: number | null;
  /** The day's replenishment lines; `null` where the refusal does not state it. */
  readonly replenishmentUnits: number | null;
}

/** A figure the refusal states; one it does not is `null`, not nought: «the DC holds 0 pairs» would be a statement nobody made. */
const whole = (value: unknown): number | null => (typeof value === 'number' && Number.isFinite(value) ? value : null);
const text = (value: unknown): string | null => (typeof value === 'string' && value.trim() !== '' ? value : null);

/** The cells a `dc_pool_overcommitted` refusal names, or `null` for any other failure. Pure. */
export function dcPoolOvercommit(error: unknown): readonly OvercommittedCell[] | null {
  if (!(error instanceof ApiProblemError) || error.code !== DC_POOL_OVERCOMMITTED_CODE) return null;
  const named = error.problem['cells'];
  if (!Array.isArray(named)) return [];
  return named.flatMap((one): OvercommittedCell[] => {
    if (typeof one !== 'object' || one === null) return [];
    const cell = one as Record<string, unknown>;
    const article = cell['articleKey'];
    const size = cell['sizeKey'];
    if (typeof article !== 'string' || typeof size !== 'string') return [];
    return [{
      articleKey: article, articleName: text(cell['articleName']), articleNameAbsentReason: text(cell['articleNameAbsentReason']),
      sizeKey: size, sizeCode: text(cell['sizeCode']), sizeScale: text(cell['sizeScale']), sizeAbsentReason: text(cell['sizeAbsentReason']),
      committedUnits: whole(cell['committedUnits']),
      dcUnits: whole(cell['dcUnits']), replenishmentUnits: whole(cell['replenishmentUnits']),
    }];
  });
}

/** The platform's reasons an article has no name here, and the words each reads as. */
const ARTICLE_ABSENT_KEYS: Readonly<Record<string, string>> = {
  attributes_not_landed: 'planning.dcOvercommit.articleAbsent.attributes_not_landed',
  not_published: 'planning.dcOvercommit.articleAbsent.not_published',
};

/** How many cells are written out before «and N more». */
const CELLS_SHOWN = 8;

export interface DcPoolOvercommitNoticeProps {
  readonly error: unknown;
  readonly cells: readonly OvercommittedCell[];
}

/** The refusal in words and the cells it concerns, each with the pairs the distribution centre holds and what claims them. */
export function DcPoolOvercommitNotice({ error, cells }: DcPoolOvercommitNoticeProps): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const sizeText = useSizeText();
  // The platform names each cell: «Sandalia Rosa (101101C3)», its size with its scale. A name it does not hold is said by its reason.
  const articleWords = (cell: OvercommittedCell): string => (cell.articleName === null
    ? t('planning.dcOvercommit.articleNoName', { code: cell.articleKey, reason: t(ARTICLE_ABSENT_KEYS[cell.articleNameAbsentReason ?? ''] ?? 'planning.dcOvercommit.articleAbsent.unknown') })
    : t('planning.dcOvercommit.article', { name: cell.articleName, code: cell.articleKey }));
  const sizeWords = (cell: OvercommittedCell): string => {
    const named = sizeNameAliased(cell);
    return named.code === null ? t(sizeAbsentReasonKey(named) ?? 'size.absent.label') : sizeText(named, true);
  };
  const problemText = useProblemMessage();
  const figure = (value: number | null): string => (value === null ? t('absent.notStated') : formatInteger(value, i18n.language));
  const more = cells.length - CELLS_SHOWN;
  return (
    <div role="alert" className="flex flex-col gap-1 max-w-prose" data-dc-pool-overcommitted={cells.length}>
      <p className="text-body text-status-critical-strong font-semibold"><span aria-hidden className="me-1">!</span>{problemText(error)}</p>
      {cells.length === 0 ? null : (
        <>
          <p className="text-caption text-ink-secondary">{t('planning.dcOvercommit.cells', { count: cells.length, figure: figure(cells.length) })}</p>
          <ul className="text-caption list-disc ps-4" data-dc-pool-overcommitted-cells="">
            {cells.slice(0, CELLS_SHOWN).map((cell) => (
              <li key={`${cell.articleKey}|${cell.sizeKey}`}>
                {t('planning.dcOvercommit.cell', {
                  article: articleWords(cell), size: sizeWords(cell),
                  dc: figure(cell.dcUnits), dcCount: cell.dcUnits ?? 0,
                  committed: figure(cell.committedUnits), replenishment: figure(cell.replenishmentUnits),
                })}
              </li>
            ))}
            {more > 0 ? <li className="list-none">{t('planning.dcOvercommit.more', { count: more, figure: figure(more) })}</li> : null}
          </ul>
        </>
      )}
    </div>
  );
}
